import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import * as Sentry from '@sentry/vue';

import PrimeVue from 'primevue/config';
import Aura from '@primeuix/themes/aura';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import es from './locales/es.json';
import pt from './locales/pt.json';
import de from './locales/de.json';
import nl from './locales/nl.json';
import { definePreset } from '@primeuix/themes';
import AuthWatcher from './views/AuthWatcher.vue';
import { setLocale } from 'yup';
import Editor from 'primevue/editor';
import { callAPI, DRUPAL_API_ROUTE } from './service/DrupalService';

import '@/assets/tailwind.css';
import '@/assets/styles.scss';
import 'leaflet/dist/leaflet.css';

const sentryEnv = import.meta.env.VITE_SENTRY_ENV;

async function getUserLanguage() {
    var request = await callAPI(DRUPAL_API_ROUTE.USER_LANGUAGE);
    return request.data;
}

getUserLanguage().then((lang) => {
    setLanguage(lang);
    i18n.global.locale.value = lang;
});

const convertJsonToYupLocale = (json) => ({
    mixed: {
        required: json.validation?.required,
        notOneOf: json.validation?.notOneOf
    },
    string: {
        email: json.validation?.email,
        min: json.validation?.min,
        max: json.validation?.max,
        matches: json.validation?.matches
    },
    array: {
        min: json.validation?.arrayMin
    }
});

const setLanguage = (lang) => {
    switch (lang) {
        case 'fr':
            lang = convertJsonToYupLocale(fr);
            break;
        case 'it':
            lang = convertJsonToYupLocale(it);
            break;
        case 'es':
            lang = convertJsonToYupLocale(es);
            break;
        case 'pt':
            lang = convertJsonToYupLocale(pt);
            break;
        case 'de':
            lang = convertJsonToYupLocale(de);
            break;
        case 'nl':
            lang = convertJsonToYupLocale(nl);
            break;
        case 'en':
        default:
            lang = convertJsonToYupLocale(en);
            break;
    }
    setLocale(lang);
};

const pinia = createPinia();
const app = createApp(App);
const i18n = createI18n({
    locale: 'en',
    fallbackLocale: 'en',
    messages: { fr, es, it, pt, en, de, nl },
    legacy: false
});

if (JSON.parse(localStorage.getItem('darkMode')) == true) document.documentElement.classList.toggle('app-dark');

// Sentry must not receive personal data: no IP, no browser/OS fingerprint (User-Agent),
// no cookies/referer, no query strings (they may carry tokens), no component props.
const stripQuery = (url) => (typeof url === 'string' ? url.split(/[?#]/)[0] : url);

const anonymizeEvent = (event) => {
    delete event.user;
    if (event.request) {
        delete event.request.headers;
        delete event.request.cookies;
        delete event.request.query_string;
        event.request.url = stripQuery(event.request.url);
    }
    if (event.contexts) {
        delete event.contexts.browser;
        delete event.contexts.os;
        delete event.contexts.device;
        delete event.contexts.culture;
    }
    return event;
};

const anonymizeBreadcrumb = (breadcrumb) => {
    if (breadcrumb.data) {
        ['url', 'from', 'to'].forEach((key) => {
            if (breadcrumb.data[key]) breadcrumb.data[key] = stripQuery(breadcrumb.data[key]);
        });
    }
    return breadcrumb;
};

Sentry.init({
    environment: sentryEnv,
    app,
    dsn: 'https://501d4c8f2ff0f70565b2787906f3e76d@o4507030412525568.ingest.de.sentry.io/4508122860683344',
    integrations: [Sentry.browserTracingIntegration({ router })],
    dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: false,
        urlQueryParams: false
    },
    attachProps: false,
    beforeSend: anonymizeEvent,
    beforeBreadcrumb: anonymizeBreadcrumb,
    tracesSampleRate: sentryEnv == 'production' ? 0.2 : 1.0,
    tracePropagationTargets: []
});

const Noir = definePreset(Aura, {
    semantic: {
        // Brand color #6333DD (600), lighter / darker shades around it.
        primary: {
            50: '#f7f5fd',
            100: '#ece7fb',
            200: '#d8ccf7',
            300: '#b9a3f0',
            400: '#9270e7',
            500: '#764be1',
            600: '#6333dd',
            700: '#542bbc',
            800: '#45249b',
            900: '#361c7a',
            950: '#281458'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.600}',
                    inverseColor: '#ffffff',
                    hoverColor: '{primary.700}',
                    activeColor: '{primary.800}'
                },
                highlight: {
                    background: '{primary.600}',
                    focusBackground: '{primary.700}',
                    color: '#ffffff',
                    focusColor: '#ffffff'
                },
                // Hovers (menus, lists, dropdown options, --surface-hover): the Drupal
                // theme bg-secondary (#f5f4fe) a shade darker, to stand out from the page.
                content: {
                    hoverBackground: '#e4e2fc'
                },
                list: {
                    option: {
                        focusBackground: '#e4e2fc'
                    }
                },
                navigation: {
                    item: {
                        focusBackground: '#e4e2fc',
                        activeBackground: '#e4e2fc'
                    }
                }
            },
            dark: {
                primary: {
                    color: '{primary.50}',
                    inverseColor: '{primary.950}',
                    hoverColor: '{primary.100}',
                    activeColor: '{primary.200}'
                },
                highlight: {
                    background: 'rgba(250, 250, 250, .16)',
                    focusBackground: 'rgba(250, 250, 250, .24)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                }
            }
        }
    },
    components: {
        speeddial: {
            root: {
                transitionDuration: '0.1s'
            }
        },
        // Secondary buttons in a light violet of the palette (dark violet text).
        button: {
            colorScheme: {
                light: {
                    root: {
                        secondary: {
                            background: '{primary.100}',
                            hoverBackground: '{primary.200}',
                            activeBackground: '{primary.300}',
                            borderColor: '{primary.100}',
                            hoverBorderColor: '{primary.200}',
                            activeBorderColor: '{primary.300}',
                            color: '{primary.700}',
                            hoverColor: '{primary.800}',
                            activeColor: '{primary.900}',
                            focusRing: {
                                color: '{primary.700}'
                            }
                        }
                    }
                }
            }
        },
        tooltip: {
            colorScheme: {
                light: {
                    root: {
                        background: '#5f5bf0',
                        color: '#ffffff'
                    }
                }
            }
        }
    }
});

app.use(pinia);
app.use(router);
app.use(PrimeVue, {
    theme: {
        preset: Noir,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);
app.use(i18n);
app.component('AuthWatcher', AuthWatcher);
app.component('Editor', Editor);

app.mount('#app');
